import React, { useEffect, useState } from 'react';
import { playersAPI, attendanceAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { CalendarIcon, Check, X, Clock, Users, Calendar as CalendarWeek, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';
import { cn } from '../lib/utils';

export default function AttendancePage() {
  const [players, setPlayers] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [weeklyData, setWeeklyData] = useState(null);
  const [monthlyData, setMonthlyData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState('daily'); // 'daily', 'weekly', or 'monthly'
  const [filterModal, setFilterModal] = useState({ open: false, type: null }); // type: 'unmarked', 'absent', 'excused'

  const dateStr = format(selectedDate, 'yyyy-MM-dd');

  useEffect(() => {
    loadData();
  }, [dateStr, viewMode]);

  const loadData = async () => {
    setLoading(true);
    try {
      const playersRes = await playersAPI.getAll();
      setPlayers(playersRes.data);

      if (viewMode === 'daily') {
        const attendanceRes = await attendanceAPI.getAll(dateStr);
        const attendanceMap = {};
        attendanceRes.data.forEach(record => {
          attendanceMap[record.player_id] = record.status;
        });
        setAttendance(attendanceMap);
      } else if (viewMode === 'weekly') {
        const weeklyRes = await attendanceAPI.getWeekly();
        setWeeklyData(weeklyRes.data);
      } else if (viewMode === 'monthly') {
        const monthlyRes = await attendanceAPI.getMonthly();
        setMonthlyData(monthlyRes.data);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (playerId, status) => {
    const previousStatus = attendance[playerId];
    setAttendance(prev => ({ ...prev, [playerId]: status }));

    try {
      await attendanceAPI.log({
        player_id: playerId,
        status,
        date: dateStr
      });
    } catch (error) {
      setAttendance(prev => ({ ...prev, [playerId]: previousStatus }));
      toast.error('Failed to save attendance');
    }
  };

  const handleMarkAll = async (status) => {
    setSaving(true);
    try {
      const records = players.map(player => ({
        player_id: player.id,
        status,
        date: dateStr
      }));
      
      await attendanceAPI.logBulk(records);
      
      const newAttendance = {};
      players.forEach(p => { newAttendance[p.id] = status; });
      setAttendance(newAttendance);
      
      toast.success(`All players marked ${status}`);
    } catch (error) {
      toast.error('Failed to mark all');
    } finally {
      setSaving(false);
    }
  };

  const stats = {
    present: Object.values(attendance).filter(s => s === 'present').length,
    absent: Object.values(attendance).filter(s => s === 'absent').length,
    excused: Object.values(attendance).filter(s => s === 'excused').length,
    unmarked: players.length - Object.keys(attendance).length
  };

  const attendanceRate = players.length > 0 
    ? Math.min(100, Math.round((stats.present / players.length) * 100))
    : 0;

  // Get filtered players based on type
  const getFilteredPlayers = (type) => {
    return players.filter(player => {
      if (type === 'unmarked') return !attendance[player.id];
      return attendance[player.id] === type;
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'present': return <Check className="w-4 h-4 text-[#32D74B]" />;
      case 'absent': return <X className="w-4 h-4 text-[#FF3B30]" />;
      case 'excused': return <Clock className="w-4 h-4 text-[#007AFF]" />;
      default: return <span className="w-4 h-4 block bg-[#27272A] rounded-full" />;
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'present': return 'bg-[#32D74B]';
      case 'absent': return 'bg-[#FF3B30]';
      case 'excused': return 'bg-[#007AFF]';
      default: return 'bg-[#27272A]';
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <p className="overline">Daily Tracking</p>
            <h1 className="font-display text-4xl lg:text-5xl text-white mt-2">ATTENDANCE</h1>
          </div>

          <div className="flex items-center gap-3">
            {/* View Mode Toggle */}
            <Tabs value={viewMode} onValueChange={setViewMode} className="w-auto">
              <TabsList className="bg-[#121212] border border-[#27272A]">
                <TabsTrigger 
                  value="daily" 
                  className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white"
                  data-testid="daily-view-tab"
                >
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  Daily
                </TabsTrigger>
                <TabsTrigger 
                  value="weekly" 
                  className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white"
                  data-testid="weekly-view-tab"
                >
                  <CalendarWeek className="w-4 h-4 mr-2" />
                  Weekly
                </TabsTrigger>
                <TabsTrigger 
                  value="monthly" 
                  className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white"
                  data-testid="monthly-view-tab"
                >
                  <CalendarWeek className="w-4 h-4 mr-2" />
                  Monthly
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {viewMode === 'daily' && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="border-[#27272A] bg-[#121212] text-white hover:bg-[#1A1A1A]"
                    data-testid="date-picker-btn"
                  >
                    <CalendarIcon className="w-4 h-4 mr-2" />
                    {format(selectedDate, 'MMM d, yyyy')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#121212] border-[#27272A]" align="end">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    className="bg-[#121212]"
                    data-testid="attendance-calendar"
                  />
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>

        {/* Stats Row - Daily View Only - Clickable Cards */}
        {viewMode === 'daily' && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Card className="bg-[#121212] border-[#27272A]" data-testid="stat-present">
              <CardContent className="p-4 text-center">
                <p className="font-display text-3xl text-[#32D74B]">{stats.present}</p>
                <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Present</p>
              </CardContent>
            </Card>
            <Card 
              className="bg-[#121212] border-[#27272A] cursor-pointer hover:border-[#FF3B30] transition-colors" 
              data-testid="stat-absent"
              onClick={() => stats.absent > 0 && setFilterModal({ open: true, type: 'absent' })}
            >
              <CardContent className="p-4 text-center">
                <p className="font-display text-3xl text-[#FF3B30]">{stats.absent}</p>
                <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Absent</p>
                {stats.absent > 0 && <p className="text-xs text-[#FF3B30] mt-1">Click to view</p>}
              </CardContent>
            </Card>
            <Card 
              className="bg-[#121212] border-[#27272A] cursor-pointer hover:border-[#007AFF] transition-colors" 
              data-testid="stat-excused"
              onClick={() => stats.excused > 0 && setFilterModal({ open: true, type: 'excused' })}
            >
              <CardContent className="p-4 text-center">
                <p className="font-display text-3xl text-[#007AFF]">{stats.excused}</p>
                <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Excused</p>
                {stats.excused > 0 && <p className="text-xs text-[#007AFF] mt-1">Click to view</p>}
              </CardContent>
            </Card>
            <Card 
              className="bg-[#121212] border-[#27272A] cursor-pointer hover:border-[#FFD60A] transition-colors" 
              data-testid="stat-unmarked"
              onClick={() => stats.unmarked > 0 && setFilterModal({ open: true, type: 'unmarked' })}
            >
              <CardContent className="p-4 text-center">
                <p className="font-display text-3xl text-[#FFD60A]">{stats.unmarked}</p>
                <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Unmarked</p>
                {stats.unmarked > 0 && <p className="text-xs text-[#FFD60A] mt-1">Click to view</p>}
              </CardContent>
            </Card>
            <Card className="bg-[#121212] border-[#27272A]" data-testid="stat-rate">
              <CardContent className="p-4 text-center">
                <p className={cn(
                  "font-display text-3xl",
                  attendanceRate >= 80 ? "text-[#32D74B]" : attendanceRate >= 50 ? "text-[#FFD60A]" : "text-[#FF3B30]"
                )}>
                  {attendanceRate}%
                </p>
                <p className="text-xs text-[#71717A] uppercase tracking-wider mt-1">Rate</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Quick Actions - Daily View Only */}
        {viewMode === 'daily' && (
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => handleMarkAll('present')}
              disabled={saving || players.length === 0}
              className="bg-[#32D74B]/10 text-[#32D74B] border border-[#32D74B]/30 hover:bg-[#32D74B]/20"
              data-testid="mark-all-present-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              Mark All Present
            </Button>
            <Button
              onClick={() => handleMarkAll('absent')}
              disabled={saving || players.length === 0}
              className="bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/30 hover:bg-[#FF3B30]/20"
              data-testid="mark-all-absent-btn"
            >
              <X className="w-4 h-4 mr-2" />
              Mark All Absent
            </Button>
            {stats.unmarked > 0 && (
              <Button
                onClick={async () => {
                  setSaving(true);
                  try {
                    await attendanceAPI.autoMarkAbsent();
                    toast.success(`Marked ${stats.unmarked} unmarked players as absent`);
                    loadData();
                  } catch (error) {
                    toast.error('Failed to auto-mark absent');
                  } finally {
                    setSaving(false);
                  }
                }}
                disabled={saving}
                className="bg-[#FFD60A]/10 text-[#FFD60A] border border-[#FFD60A]/30 hover:bg-[#FFD60A]/20"
                data-testid="auto-mark-absent-btn"
              >
                <AlertCircle className="w-4 h-4 mr-2" />
                End of Day: Mark {stats.unmarked} Unmarked as Absent
              </Button>
            )}
          </div>
        )}

        {/* Filter Modal - View and Action on Unmarked/Absent/Excused */}
        <Dialog open={filterModal.open} onOpenChange={(open) => setFilterModal({ ...filterModal, open })}>
          <DialogContent className="bg-[#121212] border-[#27272A] text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {filterModal.type === 'unmarked' && <AlertCircle className="w-5 h-5 text-[#FFD60A]" />}
                {filterModal.type === 'absent' && <X className="w-5 h-5 text-[#FF3B30]" />}
                {filterModal.type === 'excused' && <Clock className="w-5 h-5 text-[#007AFF]" />}
                {filterModal.type === 'unmarked' ? 'Unmarked Players' : 
                 filterModal.type === 'absent' ? 'Absent Players' : 'Excused Players'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {getFilteredPlayers(filterModal.type).map((player) => (
                <div key={player.id} className="flex items-center justify-between p-3 bg-[#1A1A1A] border border-[#27272A]">
                  <div className="flex items-center gap-2">
                    <span className="text-[#FF3B30] font-bold">#{player.jersey_number}</span>
                    <span className="text-white">{player.name}</span>
                    {player.locker_number && (
                      <span className="text-[#71717A] text-sm">(Loop #{player.locker_number})</span>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => { handleStatusChange(player.id, 'present'); }}
                      className="p-2 bg-[#32D74B]/10 text-[#32D74B] border border-[#32D74B]/30 hover:bg-[#32D74B]/20"
                      title="Mark Present"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => { handleStatusChange(player.id, 'absent'); }}
                      className="p-2 bg-[#FF3B30]/10 text-[#FF3B30] border border-[#FF3B30]/30 hover:bg-[#FF3B30]/20"
                      title="Mark Absent"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => { handleStatusChange(player.id, 'excused'); }}
                      className="p-2 bg-[#007AFF]/10 text-[#007AFF] border border-[#007AFF]/30 hover:bg-[#007AFF]/20"
                      title="Mark Excused"
                    >
                      <Clock className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              {getFilteredPlayers(filterModal.type).length === 0 && (
                <p className="text-[#71717A] text-center py-4">No players in this category</p>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Content based on view mode */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-[#71717A]">Loading...</div>
          </div>
        ) : viewMode === 'daily' ? (
          /* Daily View */
          <Card className="bg-[#121212] border-[#27272A]" data-testid="attendance-list">
            <CardHeader className="border-b border-[#27272A]">
              <CardTitle className="flex items-center gap-2 text-white">
                <Users className="w-5 h-5" />
                Team Roster - {format(selectedDate, 'EEEE, MMMM d')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {players.length > 0 ? (
                <div className="divide-y divide-[#27272A]">
                  {players.map((player) => (
                    <div 
                      key={player.id}
                      className="flex items-center justify-between p-4 hover:bg-[#1A1A1A] transition-colors"
                      data-testid={`attendance-row-${player.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#27272A] flex items-center justify-center text-white font-bold">
                          #{player.jersey_number}
                        </div>
                        <div>
                          <p className="text-white font-medium">
                            {player.name}
                            {player.locker_number && (
                              <span className="ml-2 text-[#FF3B30] text-sm">(Loop #{player.locker_number})</span>
                            )}
                          </p>
                          <p className="text-sm text-[#71717A]">{player.position}</p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <button
                          onClick={() => handleStatusChange(player.id, 'present')}
                          className={cn(
                            "attendance-btn flex items-center justify-center gap-2 px-4 border transition-all",
                            attendance[player.id] === 'present'
                              ? "bg-[#32D74B] border-[#32D74B] text-black"
                              : "bg-transparent border-[#27272A] text-[#71717A] hover:border-[#32D74B] hover:text-[#32D74B]"
                          )}
                          data-testid={`present-btn-${player.id}`}
                        >
                          <Check className="w-5 h-5" />
                          <span className="hidden sm:inline">Present</span>
                        </button>
                        <button
                          onClick={() => handleStatusChange(player.id, 'absent')}
                          className={cn(
                            "attendance-btn flex items-center justify-center gap-2 px-4 border transition-all",
                            attendance[player.id] === 'absent'
                              ? "bg-[#FF3B30] border-[#FF3B30] text-white"
                              : "bg-transparent border-[#27272A] text-[#71717A] hover:border-[#FF3B30] hover:text-[#FF3B30]"
                          )}
                          data-testid={`absent-btn-${player.id}`}
                        >
                          <X className="w-5 h-5" />
                          <span className="hidden sm:inline">Absent</span>
                        </button>
                        <button
                          onClick={() => handleStatusChange(player.id, 'excused')}
                          className={cn(
                            "attendance-btn flex items-center justify-center gap-2 px-4 border transition-all",
                            attendance[player.id] === 'excused'
                              ? "bg-[#007AFF] border-[#007AFF] text-white"
                              : "bg-transparent border-[#27272A] text-[#71717A] hover:border-[#007AFF] hover:text-[#007AFF]"
                          )}
                          data-testid={`excused-btn-${player.id}`}
                        >
                          <Clock className="w-5 h-5" />
                          <span className="hidden sm:inline">Excused</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-[#71717A]">
                  <Users className="w-16 h-16 mb-4 opacity-50" />
                  <p className="text-lg">No players in roster</p>
                  <p className="text-sm">Add players to track attendance</p>
                </div>
              )}
            </CardContent>
          </Card>
        ) : viewMode === 'weekly' ? (
          /* Weekly View */
          <Card className="bg-[#121212] border-[#27272A]" data-testid="weekly-attendance">
            <CardHeader className="border-b border-[#27272A]">
              <CardTitle className="flex items-center gap-2 text-white">
                <CalendarWeek className="w-5 h-5" />
                Weekly Overview - Current Week (Mon-Fri)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {/* Player Grid */}
              {weeklyData?.players && weeklyData.players.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#27272A]">
                        <th className="text-left p-4 text-xs text-[#71717A] uppercase tracking-wider font-bold">Player</th>
                        {weeklyData.summary?.map((day, idx) => (
                          <th key={idx} className="p-4 text-center text-xs text-[#71717A] uppercase tracking-wider font-bold w-20">
                            <div>{day.day}</div>
                            <div className="text-[10px] font-normal">{day.date ? format(new Date(day.date), 'MM/dd') : ''}</div>
                          </th>
                        ))}
                        <th className="p-4 text-center text-xs text-[#71717A] uppercase tracking-wider font-bold w-20">
                          Rate
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {weeklyData.players.map((player) => {
                        const presentCount = player.attendance.filter(d => d.status === 'present').length;
                        const playerRate = Math.round((presentCount / 5) * 100);
                        
                        return (
                          <tr key={player.player_id} className="border-b border-[#27272A] hover:bg-[#1A1A1A]">
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                <span className="text-[#FF3B30] font-bold">#{player.jersey_number}</span>
                                <span className="text-white">
                                  {player.name}
                                  {player.locker_number && (
                                    <span className="ml-2 text-[#71717A] text-sm">(Loop #{player.locker_number})</span>
                                  )}
                                </span>
                              </div>
                            </td>
                            {player.attendance.map((day, idx) => (
                              <td key={idx} className="p-4 text-center">
                                <div className="flex justify-center">
                                  {day.status ? (
                                    <div className={cn(
                                      "w-8 h-8 rounded-sm flex items-center justify-center",
                                      getStatusBg(day.status)
                                    )}>
                                      {day.status === 'present' && <Check className="w-4 h-4 text-black" />}
                                      {day.status === 'absent' && <X className="w-4 h-4 text-white" />}
                                      {day.status === 'excused' && <Clock className="w-4 h-4 text-white" />}
                                    </div>
                                  ) : (
                                    <div className="w-8 h-8 rounded-sm bg-[#27272A] flex items-center justify-center">
                                      <span className="text-[#71717A] text-xs">-</span>
                                    </div>
                                  )}
                                </div>
                              </td>
                            ))}
                            <td className="p-4 text-center">
                              <span className={cn(
                                "font-bold",
                                playerRate >= 80 ? "text-[#32D74B]" : playerRate >= 50 ? "text-[#FFD60A]" : "text-[#FF3B30]"
                              )}>
                                {playerRate}%
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-[#71717A]">
                  <Users className="w-16 h-16 mb-4 opacity-50" />
                  <p className="text-lg">No attendance data</p>
                </div>
              )}

              {/* Legend */}
              <div className="p-4 border-t border-[#27272A] flex items-center justify-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-[#32D74B] rounded-sm" />
                  <span className="text-xs text-[#71717A]">Present</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-[#FF3B30] rounded-sm" />
                  <span className="text-xs text-[#71717A]">Absent</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-[#007AFF] rounded-sm" />
                  <span className="text-xs text-[#71717A]">Excused</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-[#27272A] rounded-sm" />
                  <span className="text-xs text-[#71717A]">Not Marked</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Monthly View */
          <Card className="bg-[#121212] border-[#27272A]" data-testid="monthly-attendance">
            <CardHeader className="border-b border-[#27272A]">
              <CardTitle className="flex items-center gap-2 text-white">
                <CalendarWeek className="w-5 h-5" />
                Monthly Overview - Last 30 Days
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {/* Daily Attendance Percentage Summary */}
              {monthlyData?.summary && (
                <div className="p-6 border-b border-[#27272A]">
                  <p className="text-xs text-[#71717A] uppercase tracking-wider mb-4">Daily Attendance Rate</p>
                  <div className="grid grid-cols-7 gap-2">
                    {monthlyData.summary.slice(-7).map((day, idx) => (
                      <div key={idx} className="text-center p-2 bg-[#1A1A1A] border border-[#27272A]">
                        <p className="text-xs text-[#71717A]">{day.day}</p>
                        <p className={cn(
                          "font-bold text-lg",
                          day.rate >= 80 ? "text-[#32D74B]" : day.rate >= 50 ? "text-[#FFD60A]" : "text-[#FF3B30]"
                        )}>
                          {day.rate}%
                        </p>
                        <p className="text-xs text-[#71717A]">{day.present}/{day.total}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Player Grid - Left aligned */}
              {monthlyData?.players && monthlyData.players.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#27272A]">
                        <th className="text-left p-4 text-xs text-[#71717A] uppercase tracking-wider font-bold">Player</th>
                        <th className="text-left p-4 text-xs text-[#71717A] uppercase tracking-wider font-bold w-20">Present</th>
                        <th className="text-left p-4 text-xs text-[#71717A] uppercase tracking-wider font-bold w-20">Absent</th>
                        <th className="text-left p-4 text-xs text-[#71717A] uppercase tracking-wider font-bold w-20">Excused</th>
                        <th className="text-left p-4 text-xs text-[#71717A] uppercase tracking-wider font-bold w-20">Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyData.players.map((player) => (
                        <tr key={player.player_id} className="border-b border-[#27272A] hover:bg-[#1A1A1A]">
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <span className="text-[#FF3B30] font-bold">#{player.jersey_number}</span>
                              <span className="text-white">
                                {player.name}
                                {player.locker_number && (
                                  <span className="ml-2 text-[#71717A] text-sm">(Loop #{player.locker_number})</span>
                                )}
                              </span>
                            </div>
                          </td>
                          <td className="p-4 text-left">
                            <span className="text-[#32D74B] font-medium">{player.present_count}</span>
                          </td>
                          <td className="p-4 text-left">
                            <span className="text-[#FF3B30] font-medium">{player.absent_count}</span>
                          </td>
                          <td className="p-4 text-left">
                            <span className="text-[#007AFF] font-medium">{player.excused_count}</span>
                          </td>
                          <td className="p-4 text-left">
                            <span className={cn(
                              "font-bold text-lg",
                              player.attendance_rate >= 80 ? "text-[#32D74B]" : player.attendance_rate >= 50 ? "text-[#FFD60A]" : "text-[#FF3B30]"
                            )}>
                              {player.attendance_rate}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-[#71717A]">
                  <Users className="w-16 h-16 mb-4 opacity-50" />
                  <p className="text-lg">No attendance data</p>
                </div>
              )}

              {/* Legend */}
              <div className="p-4 border-t border-[#27272A] flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-[#32D74B] rounded-sm" />
                  <span className="text-xs text-[#71717A]">Present</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-[#FF3B30] rounded-sm" />
                  <span className="text-xs text-[#71717A]">Absent</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-[#007AFF] rounded-sm" />
                  <span className="text-xs text-[#71717A]">Excused</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
